/**
 * Graph visualization component using D3.js and Pixi.js
 * Adapted from Quartz-4 for standalone notes website
 */

import type {
  SimulationNodeDatum,
  SimulationLinkDatum,
  Simulation,
  ZoomBehavior,
} from "d3";
import {
  forceSimulation,
  forceManyBody,
  forceLink,
  forceCollide,
  forceRadial,
  forceX,
  forceY,
  zoomIdentity,
  select,
  drag,
  zoom,
} from "d3";
import { Text, Graphics, Application, Container, Circle, Polygon } from "pixi.js";
import { Group as TweenGroup, Tween as Tweened } from "@tweenjs/tween.js";

import type {
  NoteNode,
  GraphData,
  GraphConfig,
  NoteOpenCallback,
} from "./types";
import {
  getCSSVariable,
  getColor,
  fetchJSON,
  requireElement,
  waitForDOM,
  parseConfig,
  getLocalStorageJSON,
  setLocalStorageJSON,
  isWebGLSupported,
} from "./utils";

// ============================================================================
// Types for Rendering
// ============================================================================

interface GraphicsInfo {
  color: string;
  gfx: Graphics;
  alpha: number;
  active: boolean;
}

interface NodeData extends NoteNode, SimulationNodeDatum {}

interface LinkData extends SimulationLinkDatum<NodeData> {
  source: NodeData;
  target: NodeData;
}

interface LinkRenderData extends GraphicsInfo {
  simulationData: LinkData;
  hitArea?: Polygon;
}

interface NodeRenderData extends GraphicsInfo {
  simulationData: NodeData;
  label: Text;
}

interface TweenNode {
  update: (time: number) => void;
  stop: () => void;
}

// ============================================================================
// Constants & Configuration
// ============================================================================

const VISITED_STORAGE_KEY = "notes-graph-visited";

const DEFAULT_CONFIG: GraphConfig = {
  drag: true,
  zoom: true,
  scale: 0.5,
  // tuned for a more even, organic spread
  repelForce: 0.7, // was 0.2
  centerForce: 0.0,
  linkDistance: 50, // base (actual per-link distance is computed)
  fontSize: 0.7,
  opacityScale: 1.0,
  showTags: false,
  focusOnHover: true,
  enableRadial: false,
  removeOrphans: true,
};

const LINK_HIT_WIDTH = 12; // px, in screen space

// ============================================================================
// Visited Tracking
// ============================================================================

function getVisited(): Set<string> {
  const visited = getLocalStorageJSON<string[]>(VISITED_STORAGE_KEY, []);
  return new Set(visited);
}

function addToVisited(slug: string): void {
  const visited = getVisited();
  visited.add(slug);
  setLocalStorageJSON(VISITED_STORAGE_KEY, Array.from(visited));
}

// ============================================================================
// Graph Rendering
// ============================================================================

async function renderGraph(
  container: HTMLElement,
  config: GraphConfig,
  onNoteClick: NoteOpenCallback
): Promise<() => void> {
  // Clear container
  container.innerHTML = "";

  // Check WebGL support
  if (!isWebGLSupported()) {
    container.innerHTML =
      '<div style="color: var(--muted); padding: 2rem; text-align: center;">WebGL is not supported in your browser. Graph visualization requires WebGL.</div>';
    return () => {};
  }

  // Load graph data
  let graphData: GraphData;
  try {
    graphData = await fetchJSON<GraphData>("notes-data/graph.json");
  } catch (error) {
    container.innerHTML =
      '<div style="color: var(--muted); padding: 2rem; text-align: center;">Failed to load graph data. Please run the build script.</div>';
    return () => {};
  }

  const visited = getVisited();

  // Extract configuration
  const {
    drag: enableDrag,
    zoom: enableZoom,
    scale,
    repelForce,
    linkDistance, // used as base in distance fn
    fontSize,
    opacityScale,
    focusOnHover,
    enableRadial,
    removeOrphans,
  } = config;

  // Filter out orphan nodes if configured
  let nodes = graphData.nodes;
  let links = graphData.links;

  if (removeOrphans) {
    const connectedNodes = new Set<string>();
    links.forEach((link) => {
      const sourceId =
        typeof link.source === "string" ? link.source : link.source.id;
      const targetId =
        typeof link.target === "string" ? link.target : link.target.id;
      connectedNodes.add(sourceId);
      connectedNodes.add(targetId);
    });
    nodes = nodes.filter((n) => connectedNodes.has(n.id));
  }

  // Compute degrees
  const degreeMap = new Map<string, number>();
  links.forEach((link) => {
    const s = typeof link.source === "string" ? link.source : link.source.id;
    const t = typeof link.target === "string" ? link.target : link.target.id;
    degreeMap.set(s, (degreeMap.get(s) || 0) + 1);
    degreeMap.set(t, (degreeMap.get(t) || 0) + 1);
  });
  nodes.forEach((n) => (n.degree = degreeMap.get(n.id) || 0));

  // Prepare simulation data
  const nodeData: NodeData[] = nodes.map((n) => ({ ...n }));
  const nodeById = new Map(nodeData.map((n) => [n.id, n]));
  const linkData: LinkData[] = links.map((l) => ({
    source: nodeById.get(
      typeof l.source === "string" ? l.source : l.source.id
    )!,
    target: nodeById.get(
      typeof l.target === "string" ? l.target : l.target.id
    )!,
  }));

  const width = container.offsetWidth;
  const height = Math.max(container.offsetHeight, 250);

  // --- Fit-to-view helpers ---------------------------------------------------
  function computeBounds() {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const n of nodeData) {
      if (n.x == null || n.y == null) continue;
      if (n.x < minX) minX = n.x;
      if (n.x > maxX) maxX = n.x;
      if (n.y < minY) minY = n.y;
      if (n.y > maxY) maxY = n.y;
    }
    if (!isFinite(minX)) return null;

    // convert to screen-space (we add width/2, height/2 when drawing)
    const x0 = minX + width / 2;
    const y0 = minY + height / 2;
    const x1 = maxX + width / 2;
    const y1 = maxY + height / 2;

    return { x0, y0, x1, y1 };
  }

  let zoomBehavior: ZoomBehavior<HTMLCanvasElement, NodeData> | null = null;
  let currentTransform = zoomIdentity;

  function fitToView(padding = 40) {
    const b = computeBounds();
    if (!b) return;

    const bw = Math.max(1, b.x1 - b.x0);
    const bh = Math.max(1, b.y1 - b.y0);
    const k = Math.min((width - 2 * padding) / bw, (height - 2 * padding) / bh);
    const kClamped = Math.max(0.25, Math.min(4, k));
    const tx = width / 2 - (kClamped * (b.x0 + b.x1)) / 2;
    const ty = height / 2 - (kClamped * (b.y0 + b.y1)) / 2;

    const t = zoomIdentity.translate(tx, ty).scale(kClamped);
    currentTransform = t;

    if (zoomBehavior) {
      select<HTMLCanvasElement, NodeData>(app.canvas).call(
        (zoomBehavior as ZoomBehavior<HTMLCanvasElement, NodeData>).transform,
        t
      );
    } else {
      // fallback if zoom is disabled
      stage.scale.set(kClamped, kClamped);
      stage.position.set(tx, ty);
    }
  }
  // ---------------------------------------------------------------------------

  // Create D3 force simulation — tuned for a more uniform spread
  const base = Math.max(30, linkDistance); // fallback to config
  const perDeg = 1.5;
  const collisionPad = 2;

  const simulation: Simulation<NodeData, LinkData> =
    forceSimulation<NodeData>(nodeData)
      .alpha(1)
      .alphaDecay(0.02)
      .velocityDecay(0.35)
      .force(
        "charge",
        forceManyBody()
          .strength(-220 * repelForce)
          .distanceMin(10)
          .distanceMax(Math.max(width, height))
      )
      .force(
        "link",
        forceLink<NodeData, LinkData>(linkData)
          .id((d: any) => d.id)
          .distance(
            (l) =>
              base +
              perDeg *
                (((l.source as NodeData).degree || 0) +
                  ((l.target as NodeData).degree || 0))
          )
          .strength(0.4)
      )
      .force(
        "collide",
        forceCollide<NodeData>((n) => nodeRadius(n) + collisionPad)
          .strength(0.9)
          .iterations(2)
      )
      .force("x", forceX<NodeData>(0).strength(0.03))
      .force("y", forceY<NodeData>(0).strength(0.03));

  const radius = (Math.min(width, height) / 2) * 0.8;
  if (enableRadial) {
    simulation.force("radial", forceRadial(radius).strength(0.1));
  }

  // Warm-up ticks so it looks settled on first paint, then fit
  for (let i = 0; i < 150; i++) simulation.tick();
  simulation.on("end", () => fitToView()); // also fit after natural end

  // Colors
  const colorFg = getColor("--fg", "#ebe7e2");
  const colorFgSoft = getColor("--fg-soft", "#cfc9c1");
  const colorMuted = getColor("--muted", "#8c8780");
  const colorAccent = getColor("--accent", "#ebe7e2");

  // Node coloring
  const nodeColor = (n: NodeData): string =>
    visited.has(n.id) ? colorAccent : colorFg;

  // Node radius
  function nodeRadius(n: NodeData): number {
    return 4 + Math.sqrt(n.degree || 1);
  }

  // Hover state
  let hoveredNodeId: string | null = null;
  let hoveredLinkData: LinkData | null = null; // Track hovered link for edge dragging
  let hoveredNeighbours: Set<string> = new Set();
  const linkRenderData: LinkRenderData[] = [];
  const nodeRenderData: NodeRenderData[] = [];
  const tweens = new Map<string, TweenNode>();

  function distanceSqToSegment(
    px: number,
    py: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    if (dx === 0 && dy === 0) {
      const sx = px - x1;
      const sy = py - y1;
      return sx * sx + sy * sy;
    }
    const t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);
    const clamped = Math.max(0, Math.min(1, t));
    const cx = x1 + clamped * dx;
    const cy = y1 + clamped * dy;
    const sx = px - cx;
    const sy = py - cy;
    return sx * sx + sy * sy;
  }

  function findClosestLink(
    px: number,
    py: number,
    maxDist: number
  ): LinkData | null {
    let best: LinkData | null = null;
    let bestDist = maxDist * maxDist;

    for (const l of linkRenderData) {
      const linkD = l.simulationData;
      if (linkD.source.x == null || linkD.source.y == null) continue;
      if (linkD.target.x == null || linkD.target.y == null) continue;

      const x1 = linkD.source.x + width / 2;
      const y1 = linkD.source.y + height / 2;
      const x2 = linkD.target.x + width / 2;
      const y2 = linkD.target.y + height / 2;

      const d2 = distanceSqToSegment(px, py, x1, y1, x2, y2);
      if (d2 <= bestDist) {
        bestDist = d2;
        best = linkD;
      }
    }

    return best;
  }

  function updateHoverInfo(newHoveredId: string | null) {
    hoveredNodeId = newHoveredId;

    if (newHoveredId === null) {
      hoveredNeighbours = new Set();
      nodeRenderData.forEach((n) => (n.active = false));
      linkRenderData.forEach((l) => (l.active = false));
    } else {
      hoveredNeighbours = new Set();
      linkRenderData.forEach((l) => {
        const linkD = l.simulationData;
        if (
          linkD.source.id === newHoveredId ||
          linkD.target.id === newHoveredId
        ) {
          hoveredNeighbours.add(linkD.source.id);
          hoveredNeighbours.add(linkD.target.id);
        }
        l.active =
          linkD.source.id === newHoveredId || linkD.target.id === newHoveredId;
      });

      nodeRenderData.forEach((n) => {
        n.active = hoveredNeighbours.has(n.simulationData.id);
      });
    }
  }

  let dragStartTime = 0;
  let dragging = false;

  function renderLinks() {
    tweens.get("link")?.stop();
    const tweenGroup = new TweenGroup();

    linkRenderData.forEach((l) => {
      let alpha = 1;
      if (hoveredNodeId) alpha = l.active ? 1 : 0.2;
      l.color = l.active ? colorMuted : colorFgSoft;
      tweenGroup.add(new Tweened<LinkRenderData>(l).to({ alpha }, 200));
    });

    tweenGroup.getAll().forEach((tw) => tw.start());
    tweens.set("link", {
      update: tweenGroup.update.bind(tweenGroup),
      stop() {
        tweenGroup.getAll().forEach((tw) => tw.stop());
      },
    });
  }

  function renderLabels() {
    tweens.get("label")?.stop();
    const tweenGroup = new TweenGroup();

    const defaultScale = 1 / scale;
    const activeScale = defaultScale * 1.1;

    nodeRenderData.forEach((n) => {
      const nodeId = n.simulationData.id;
      if (hoveredNodeId === nodeId) {
        tweenGroup.add(
          new Tweened<Text>(n.label).to(
            {
              alpha: 1,
              scale: { x: activeScale, y: activeScale },
            },
            100
          )
        );
      } else {
        tweenGroup.add(
          new Tweened<Text>(n.label).to(
            {
              alpha: n.label.alpha,
              scale: { x: defaultScale, y: defaultScale },
            },
            100
          )
        );
      }
    });

    tweenGroup.getAll().forEach((tw) => tw.start());
    tweens.set("label", {
      update: tweenGroup.update.bind(tweenGroup),
      stop() {
        tweenGroup.getAll().forEach((tw) => tw.stop());
      },
    });
  }

  function renderNodes() {
    tweens.get("hover")?.stop();
    const tweenGroup = new TweenGroup();

    nodeRenderData.forEach((n) => {
      let alpha = 1;
      if (hoveredNodeId !== null && focusOnHover) alpha = n.active ? 1 : 0.2;
      tweenGroup.add(new Tweened<Graphics>(n.gfx, tweenGroup).to({ alpha }, 200));
    });

    tweenGroup.getAll().forEach((tw) => tw.start());
    tweens.set("hover", {
      update: tweenGroup.update.bind(tweenGroup),
      stop() {
        tweenGroup.getAll().forEach((tw) => tw.stop());
      },
    });
  }

  function renderPixiFromD3() {
    renderNodes();
    renderLinks();
    renderLabels();
  }

  function updateLinkHitArea(
    l: LinkRenderData,
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const scale = currentTransform.k || 1;
    const half = (LINK_HIT_WIDTH / scale) / 2;
    const nx = -(dy / len) * half;
    const ny = (dx / len) * half;
    const points = [
      x1 + nx,
      y1 + ny,
      x2 + nx,
      y2 + ny,
      x2 - nx,
      y2 - ny,
      x1 - nx,
      y1 - ny,
    ];

    if (!l.hitArea) {
      l.hitArea = new Polygon(points);
    } else {
      l.hitArea.points.length = 0;
      l.hitArea.points.push(...points);
    }
    l.gfx.hitArea = l.hitArea;
  }

  // Clear existing tweens
  tweens.forEach((tween) => tween.stop());
  tweens.clear();

  // Create Pixi application
  const app = new Application();
  await app.init({
    width,
    height,
    antialias: true,
    autoStart: false,
    autoDensity: true,
    backgroundAlpha: 0,
    preference: "webgpu",
    resolution: window.devicePixelRatio,
    eventMode: "static",
  });

  container.appendChild(app.canvas);

  const stage = app.stage;
  stage.interactive = false;
  stage.sortableChildren = true;

  const labelsContainer = new Container<Text>({ zIndex: 3, isRenderGroup: true });
  const nodesContainer = new Container<Graphics>({ zIndex: 2, isRenderGroup: true });
  const linkContainer = new Container<Graphics>({ zIndex: 1, isRenderGroup: true });
  stage.addChild(linkContainer, nodesContainer, labelsContainer);

  // Create nodes
  nodeData.forEach((n) => {
    const nodeId = n.id;

    const label = new Text({
      interactive: false,
      eventMode: "none",
      text: n.label,
      alpha: 0,
      anchor: { x: 0.5, y: 1.2 },
      style: {
        fontSize: fontSize * 15,
        fill: colorFg,
        fontFamily: getCSSVariable("--bodyFont") || "monospace",
      },
      resolution: window.devicePixelRatio * 4,
    });
    label.scale.set(1 / scale);

    let oldLabelOpacity = 0;
    const gfx = new Graphics({
      interactive: true,
      label: nodeId,
      eventMode: "static",
      hitArea: new Circle(0, 0, nodeRadius(n)),
      cursor: "pointer",
    })
      .circle(0, 0, nodeRadius(n))
      .fill({ color: nodeColor(n) })
      .on("pointerover", (e) => {
        hoveredLinkData = null;
        updateHoverInfo((e.target as any).label);
        oldLabelOpacity = label.alpha;
        if (!dragging) renderPixiFromD3();
      })
      .on("pointerleave", () => {
        updateHoverInfo(null);
        label.alpha = oldLabelOpacity;
        if (!dragging) renderPixiFromD3();
      });

    nodesContainer.addChild(gfx);
    labelsContainer.addChild(label);

    const nodeRenderDatum: NodeRenderData = {
      simulationData: n,
      gfx,
      label,
      color: nodeColor(n),
      alpha: 1,
      active: false,
    };

    nodeRenderData.push(nodeRenderDatum);
  });

  // Create links - make them interactive for edge dragging
  linkData.forEach((l) => {
    const gfx = new Graphics({
      interactive: true,
      eventMode: "static",
      cursor: "grab",
    })
      .on("pointerover", () => {
        hoveredLinkData = l;
        if (hoveredNodeId !== null) {
          updateHoverInfo(null);
          if (!dragging) renderPixiFromD3();
        }
      })
      .on("pointerleave", () => {
        if (hoveredLinkData === l) {
          hoveredLinkData = null;
        }
      });
    linkContainer.addChild(gfx);

    const linkRenderDatum: LinkRenderData = {
      simulationData: l,
      gfx,
      color: colorFgSoft,
      alpha: 1,
      active: false,
    };

    linkRenderData.push(linkRenderDatum);
  });

  // Enable drag - supports both node dragging and edge dragging
  if (enableDrag) {
    // Track if we're dragging an edge (both connected nodes move)
    let draggingEdge = false;
    let edgeSourceNode: NodeData | null = null;
    let edgeTargetNode: NodeData | null = null;
    let dragLinkCandidate: LinkData | null = null;

    select<HTMLCanvasElement, NodeData | undefined>(app.canvas).call(
      drag<HTMLCanvasElement, NodeData | undefined>()
        .container(() => app.canvas)
        .subject((event) => {
          // First check if hovering over a node
          const hoveredNode = nodeData.find((n) => n.id === hoveredNodeId);
          if (hoveredNode) {
            dragLinkCandidate = null;
            return hoveredNode;
          }

          // If hovering over an edge, return the source node as the subject
          // but mark that we're dragging an edge
          if (hoveredLinkData) {
            dragLinkCandidate = hoveredLinkData;
            return hoveredLinkData.source;
          }

          const px = currentTransform.invertX(event.x);
          const py = currentTransform.invertY(event.y);
          const maxDist = LINK_HIT_WIDTH / (currentTransform.k || 1);
          const nearest = findClosestLink(px, py, maxDist);
          if (nearest) {
            dragLinkCandidate = nearest;
            return nearest.source;
          }

          dragLinkCandidate = null;
          return undefined;
        })
        .on("start", function dragstarted(event) {
          if (!event.active) simulation.alphaTarget(1).restart();

          // Check if this is an edge drag
          if (dragLinkCandidate && !hoveredNodeId) {
            draggingEdge = true;
            edgeSourceNode = dragLinkCandidate.source;
            edgeTargetNode = dragLinkCandidate.target;

            // Fix both nodes
            edgeSourceNode.fx = edgeSourceNode.x;
            edgeSourceNode.fy = edgeSourceNode.y;
            edgeTargetNode.fx = edgeTargetNode.x;
            edgeTargetNode.fy = edgeTargetNode.y;

            (edgeSourceNode as any).__initialDragPos = {
              x: edgeSourceNode.x,
              y: edgeSourceNode.y,
            };
            (edgeTargetNode as any).__initialDragPos = {
              x: edgeTargetNode.x,
              y: edgeTargetNode.y,
            };
          } else {
            draggingEdge = false;
            edgeSourceNode = null;
            edgeTargetNode = null;

            event.subject.fx = event.subject.x;
            event.subject.fy = event.subject.y;
            (event.subject as any).__initialDragPos = {
              x: event.subject.x,
              y: event.subject.y,
              fx: event.subject.fx,
              fy: event.subject.fy,
            };
          }

          dragLinkCandidate = null;
          dragStartTime = Date.now();
          dragging = true;
        })
        .on("drag", function dragged(event) {
          if (draggingEdge && edgeSourceNode && edgeTargetNode) {
            // Move both connected nodes together
            const sourceInit = (edgeSourceNode as any).__initialDragPos;
            const targetInit = (edgeTargetNode as any).__initialDragPos;
            const dx = (event.x - sourceInit.x) / currentTransform.k;
            const dy = (event.y - sourceInit.y) / currentTransform.k;

            edgeSourceNode.fx = sourceInit.x + dx;
            edgeSourceNode.fy = sourceInit.y + dy;
            edgeTargetNode.fx = targetInit.x + dx;
            edgeTargetNode.fy = targetInit.y + dy;
          } else {
            const initPos = (event.subject as any).__initialDragPos;
            event.subject.fx =
              initPos.x + (event.x - initPos.x) / currentTransform.k;
            event.subject.fy =
              initPos.y + (event.y - initPos.y) / currentTransform.k;
          }
        })
        .on("end", function dragended(event) {
          if (!event.active) simulation.alphaTarget(0);

          if (draggingEdge && edgeSourceNode && edgeTargetNode) {
            // Release both nodes
            edgeSourceNode.fx = null;
            edgeSourceNode.fy = null;
            edgeTargetNode.fx = null;
            edgeTargetNode.fy = null;
            draggingEdge = false;
            edgeSourceNode = null;
            edgeTargetNode = null;
          } else {
            event.subject.fx = null;
            event.subject.fy = null;

            // If time between mousedown and mouseup is short, treat as click
            if (Date.now() - dragStartTime < 500) {
              const node = nodeData.find((n) => n.id === event.subject.id);
              if (node) {
                addToVisited(node.id);
                onNoteClick(node.id);
              }
            }
          }

          dragging = false;
        })
    );
  } else {
    // If drag disabled, use simple click handlers
    nodeRenderData.forEach((node) => {
      node.gfx.on("click", () => {
        addToVisited(node.simulationData.id);
        onNoteClick(node.simulationData.id);
      });
    });
  }

  // Enable zoom (and fit after attaching it so it can set initial transform)
  if (enableZoom) {
    zoomBehavior = zoom<HTMLCanvasElement, NodeData>()
      .extent([
        [0, 0],
        [width, height],
      ])
      .scaleExtent([0.25, 4])
      .on("zoom", ({ transform }) => {
        currentTransform = transform;
        stage.scale.set(transform.k, transform.k);
        stage.position.set(transform.x, transform.y);

        // Zoom adjusts opacity of labels
        const scaleValue = transform.k * opacityScale;
        const scaleOpacity = Math.max((scaleValue - 1) / 3.75, 0);
        const active = new Set(
          nodeRenderData.filter((n) => n.active).map((n) => n.label)
        );

        labelsContainer.children.forEach((label) => {
          // @ts-ignore PIXI typing is broad; runtime check is fine
          if (!active.has(label)) {
            // @ts-ignore
            label.alpha = scaleOpacity;
          }
        });
      });

    select<HTMLCanvasElement, NodeData>(app.canvas).call(zoomBehavior);

    // initial fit using zoom behavior
    fitToView();
  } else {
    // initial fit without zoom
    fitToView();
  }

  // Animation loop
  let stopAnimation = false;
  function animate(time: number) {
    if (stopAnimation) return;

    // Update node positions
    nodeRenderData.forEach((n) => {
      const { x, y } = n.simulationData;
      if (x == null || y == null) return;
      n.gfx.position.set(x + width / 2, y + height / 2);
      if (n.label) n.label.position.set(x + width / 2, y + height / 2);
    });

    // Update link positions
    linkRenderData.forEach((l) => {
      const linkD = l.simulationData;
      const x1 = linkD.source.x! + width / 2;
      const y1 = linkD.source.y! + height / 2;
      const x2 = linkD.target.x! + width / 2;
      const y2 = linkD.target.y! + height / 2;

      l.gfx.clear();
      l.gfx.moveTo(x1, y1);
      l.gfx.lineTo(x2, y2).stroke({ alpha: l.alpha, width: 2, color: l.color });
      updateLinkHitArea(l, x1, y1, x2, y2);
    });

    tweens.forEach((t) => t.update(time));
    app.renderer.render(stage);
    requestAnimationFrame(animate);
  }

  requestAnimationFrame(animate);

  // Return cleanup function
  return () => {
    stopAnimation = true;
    tweens.forEach((tween) => tween.stop());
    tweens.clear();
    app.destroy();
  };
}

// ============================================================================
// Initialization
// ============================================================================

export async function initGraph(): Promise<void> {
  await waitForDOM();

  const graphContainer = requireElement<HTMLElement>("#graph");
  const graphParent = graphContainer.parentElement;

  if (!graphParent) {
    console.error("Graph container has no parent element");
    return;
  }

  // Parse configuration from data attribute
  const config = parseConfig<GraphConfig>(graphParent, DEFAULT_CONFIG);

  // Get the note opening callback from global scope
  const onNoteClick: NoteOpenCallback = (slug: string) => {
    if ((window as any).openNote) {
      (window as any).openNote(slug);
    } else {
      console.warn("openNote function not found on window object");
    }
  };

  try {
    await renderGraph(graphContainer, config, onNoteClick);
  } catch (error) {
    console.error("Failed to initialize graph:", error);
    graphContainer.innerHTML =
      '<div style="color: var(--muted); padding: 2rem; text-align: center;">Failed to initialize graph visualization.</div>';
  }
}

// Auto-initialize when script loads
initGraph().catch((error) => {
  console.error("Graph initialization error:", error);
});
